import {useCallback, useState} from 'react';

import {useDeleteBuyerMutation} from './useDeleteBuyerMutation';
// Direct path, not the feature barrel — the barrel re-exports components that import hooks back.
import {showBuyerDeletedToast} from '../view/components/BuyerDeletedToast';

import type {BuyerDTO, BuyerPurchaseDTO} from '@sollapay/types';

interface BuyerDeleteTarget {
  buyer: BuyerDTO;
  purchase: BuyerPurchaseDTO;
}

/**
 * Shared open/confirm/close state machine for the buyer-deletion confirm modal — used by both
 * the buyer list (a dynamic buyer+purchase target) and the buyer detail page (a fixed buyer,
 * varying only which of their purchases is selected). `onDeleted` fires after a successful
 * deletion, once the modal has already closed — e.g. to navigate away.
 */
export function useBuyerDeleteFlow(trustAccountId: string, onDeleted?: () => void) {
  const [target, setTarget] = useState<BuyerDeleteTarget | null>(null);
  const mutation = useDeleteBuyerMutation(trustAccountId);

  const open = useCallback((buyer: BuyerDTO, purchase: BuyerPurchaseDTO) => {
    setTarget({buyer, purchase});
  }, []);

  const close = useCallback(() => {
    mutation.reset();
    setTarget(null);
  }, [mutation]);

  const confirm = useCallback(async () => {
    if (!target) return;
    try {
      await mutation.mutateAsync({purchaseId: target.purchase.id, buyerId: target.buyer.id});
      setTarget(null);
      showBuyerDeletedToast();
      onDeleted?.();
    } catch {
      // Keep the modal open so the user can see the error (via mutation.error) and retry/cancel
    }
  }, [mutation, target, onDeleted]);

  return {
    target,
    open,
    close,
    confirm,
    isLoading: mutation.isPending,
    error: mutation.error
  };
}
